import { useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import { SymbolChart } from 'common/interfaces/api';
import { cn, useMediaQuery } from 'common/utils';
import { formatCurrency, formatPercent } from 'common/utils/trading-demo';
import { CloseIcon, SearchIcon } from 'components/v2/atoms/icon';
import { useQuery } from 'react-query';
import { getAllSymbolsWithPrice } from 'service/chart';
import useGlobalStore from 'store/global';

interface ChooseSymbolDialogProps {
  isOpen: boolean;
  onSelect: (symbol: SymbolChart) => void;
  onClose: () => void;
  selectedSymbol?: SymbolChart;
}

const ChooseSymbolDialog = ({
  isOpen,
  onSelect,
  onClose,
  selectedSymbol,
}: ChooseSymbolDialogProps) => {
  const isAuthInit = useGlobalStore((state) => state.isAuthInit);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('すべて');

  const { data: symbolsData, isFetched: isFetchedSymbols } = useQuery(
    'getSymbolsListWithPrice',
    async () => {
      return await getAllSymbolsWithPrice();
    },
    {
      refetchOnWindowFocus: false,
      enabled: isAuthInit,
    },
  );

  const allSymbols: SymbolChart[] = useMemo(() => {
    return symbolsData?.data || [];
  }, [symbolsData]);

  const categories = useMemo(() => {
    return ['すべて', ...new Set(allSymbols.map((symbol) => symbol.type))];
  }, [allSymbols]);

  const filteredSymbols = allSymbols.filter((symbol) => {
    const matchesSearch =
      symbol.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      symbol.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      activeCategory === 'すべて' || symbol.type === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const handleSymbolSelect = (symbol: SymbolChart) => {
    onSelect(symbol);
    onClose();
  };

  const isSymbolSelected = (symbolId: number) => {
    return selectedSymbol?.id === symbolId;
  };

  const isMobile = useMediaQuery('(max-width: 767px)');
  if (!isOpen) return null;
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="lg"
      fullWidth={isMobile}
      PaperProps={{
        style: {
          maxHeight: 'calc(100vh - 80px)',
          borderRadius: '16px',
        },
      }}
      slotProps={{
        backdrop: {
          style: {
            backgroundColor: 'rgba(0, 0, 0, 0.30)',
          },
        },
      }}
    >
      <div className="all-font-sans w-full sm:w-[500px]">
        {/* Header */}
        <div className="border-b border-pp-border-primary bg-[#F8F9FA] p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-base font-semibold text-pp-text-primary">
              マーケットを検索
            </h3>
            <button onClick={onClose}>
              <CloseIcon
                className="size-4 text-pp-text-secondary"
                width={16}
                height={16}
              />
            </button>
          </div>
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-pp-text-secondary" />
            <input
              type="text"
              placeholder="マーケットコードまたは名前で検索"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-pp-border-primary bg-white py-2 pl-9 pr-4 text-sm focus:border-pp-text-tertiary focus:outline-none"
            />
          </div>
        </div>

        {/* Category Tabs */}
        <div className="border-b border-pp-border-primary px-4 py-2">
          <div className="scrollbar-hide flex space-x-2 overflow-x-auto">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-light transition-colors ${
                  activeCategory === category
                    ? 'bg-pp-text-primary text-white'
                    : 'bg-pp-bg-tertiary text-pp-text-primary hover:bg-pp-border-primary'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Symbols List */}
        <div className="scrollbar-default max-h-96 overflow-y-auto">
          {!isFetchedSymbols ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 8 }).map((_, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between px-4 py-2"
                >
                  <div className="flex-1">
                    <div className="mb-1 h-4 w-32 animate-pulse rounded bg-pp-bg-tertiary"></div>
                  </div>
                  <div className="flex flex-col items-end space-y-1">
                    <div className="h-4 w-16 animate-pulse rounded bg-pp-bg-tertiary"></div>
                    <div className="h-3 w-12 animate-pulse rounded bg-pp-bg-tertiary"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            filteredSymbols.map((symbol) => (
              <button
                key={symbol.id}
                onClick={() => handleSymbolSelect(symbol)}
                className={cn(
                  'flex w-full items-center px-4 py-2 text-left transition-colors hover:bg-pp-bg-tertiary',
                  {
                    'bg-pp-green-subtle': isSymbolSelected(symbol.id),
                  },
                )}
              >
                <div className="flex flex-1 items-center justify-between">
                  <div className="text-sm font-medium text-pp-text-primary">
                    {symbol.name}
                  </div>
                  <div className="flex flex-col items-end">
                    <div className="text-sm font-bold text-pp-text-primary">
                      {formatCurrency({
                        value: symbol.price?.toString(),
                        withSign: false,
                        supportZero: false,
                        currency: symbol.currency,
                        isSymbolPrice: true,
                      })}
                    </div>

                    <div
                      className={cn('text-xs font-medium', {
                        'text-pp-green-primary':
                          symbol.change && symbol.change > 0,
                        'text-pp-error-primary':
                          symbol.change && symbol.change < 0,
                        'text-pp-text-secondary':
                          !symbol.change || symbol.change === 0,
                      })}
                    >
                      {formatPercent({
                        value: symbol.change?.toString(),
                        withSign: true,
                        supportZero: true,
                      })}
                    </div>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>

        {isFetchedSymbols && filteredSymbols.length === 0 && (
          <div className="px-4 py-6 text-center text-sm text-gray-500">
            検索結果が見つかりません
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default ChooseSymbolDialog;
